import { useCallback, useRef, useState } from "react";
import { MoveHorizontal } from "lucide-react";
import { useI18n } from "@/lib/i18n";

interface BeforeAfterSliderProps {
  before: string;
  after: string;
  beforeSrcSet?: string;
  afterSrcSet?: string;
  beforeAlt: string;
  afterAlt: string;
}

/** Draggable before/after comparison with a vertical split divider. */
export function BeforeAfterSlider({ before, after, beforeAlt, afterAlt, beforeSrcSet, afterSrcSet }: BeforeAfterSliderProps) {
  const { t } = useI18n();
  const containerRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const [pos, setPos] = useState(50);

  const updateFromClientX = useCallback((clientX: number) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, pct)));
  }, []);

  const onPointerDown = (e: React.PointerEvent) => {
    draggingRef.current = true;
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    updateFromClientX(e.clientX);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!draggingRef.current) return;
    updateFromClientX(e.clientX);
  };
  const stopDrag = () => {
    draggingRef.current = false;
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft") setPos((p) => Math.max(0, p - 4));
    if (e.key === "ArrowRight") setPos((p) => Math.min(100, p + 4));
  };

  return (
    <div
      ref={containerRef}
      className="relative aspect-[4/3] w-full touch-none select-none overflow-hidden rounded-2xl border border-border shadow-sm"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={stopDrag}
      onPointerLeave={stopDrag}
    >
      {/* After (base layer) */}
      <img
        src={after}
        srcSet={afterSrcSet}
        sizes="(max-width: 768px) 100vw, 50vw"
        width={1024}
        height={768}
        alt={afterAlt}
        loading="lazy"
        draggable={false}
        className="absolute inset-0 h-full w-full object-cover"
      />
      <span className="pointer-events-none absolute right-3 top-3 rounded-full bg-accent px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-accent-foreground shadow">
        {t("gallery.after")}
      </span>

      {/* Before (clipped layer) */}
      <img
        src={before}
        srcSet={beforeSrcSet}
        sizes="(max-width: 768px) 100vw, 50vw"
        width={1024}
        height={768}
        alt={beforeAlt}
        loading="lazy"
        draggable={false}
        className="absolute inset-0 h-full w-full object-cover"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      />
      <span
        className="pointer-events-none absolute left-3 top-3 rounded-full bg-primary/85 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-primary-foreground shadow backdrop-blur"
        style={{ opacity: pos > 12 ? 1 : 0 }}
      >
        {t("gallery.before")}
      </span>

      {/* Divider + handle */}
      <div
        className="pointer-events-none absolute inset-y-0 w-0.5 bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.15)]"
        style={{ left: `${pos}%`, transform: "translateX(-50%)" }}
      />
      <button
        type="button"
        role="slider"
        aria-label={t("gallery.dragCompare")}
        aria-valuenow={Math.round(pos)}
        aria-valuemin={0}
        aria-valuemax={100}
        onKeyDown={onKeyDown}
        className="absolute top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize items-center justify-center rounded-full border-2 border-white bg-accent text-accent-foreground shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        style={{ left: `${pos}%` }}
      >
        <MoveHorizontal className="h-5 w-5" />
      </button>
    </div>
  );
}
